import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Crown, Sparkles, MapPin, ArrowRight, Building2, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRUST_TIER = {
  platinum: { Icon: Crown, color: 'text-[#D4AF37]', bg: 'bg-gradient-to-r from-amber-50 to-yellow-50' },
  gold: { Icon: ShieldCheck, color: 'text-amber-600', bg: 'bg-amber-50' },
  silver: { Icon: ShieldCheck, color: 'text-slate-500', bg: 'bg-slate-50' },
  building: { Icon: Sparkles, color: 'text-slate-400', bg: 'bg-slate-50' },
};

export default function StatePage() {
  const { key } = useParams();
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = `WakaCasa · ${language === 'es' ? 'Propiedades en' : 'Properties in'} ${key}`;
    setLoading(true);
    axios.get(`${API}/states/${key}`)
      .then(r => { setData(r.data); setNotFound(false); })
      .catch(err => { setNotFound(err.response?.status === 404); })
      .finally(() => setLoading(false));
  }, [key, language]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8" data-testid="state-page-loading">
        <Skeleton className="h-44 rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="state-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">
          {language === 'es' ? 'Estado no encontrado' : 'State not found'}
        </h1>
        <p className="text-slate-500 mt-3">
          {language === 'es' ? 'Verifica el enlace o vuelve al inicio.' : 'Check the URL or go back home.'}
        </p>
        <Link to="/"><Button className="mt-6 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]">{language === 'es' ? 'Inicio' : 'Home'}</Button></Link>
      </div>
    );
  }

  const { state, listing_count, properties, cities, top_agents } = data;
  const populatedCities = cities.filter(c => c.listing_count > 0);
  const otherCities = cities.filter(c => c.listing_count === 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 space-y-12" data-testid="state-page">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B192C] via-[#0F2440] to-[#0B192C] px-8 py-12 sm:px-14 sm:py-16">
        <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[#D4AF37]/5 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold tracking-[0.2em] uppercase mb-4">
            <MapPin className="w-3.5 h-3.5" />
            <span>{language === 'es' ? 'Estado de' : 'State of'} Venezuela</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight" data-testid="state-name">
            {language === 'es' ? 'Propiedades en' : 'Properties in'}<br /><span className="text-[#D4AF37]">{state.name}</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg mt-5 max-w-2xl">
            {language === 'es'
              ? `Descubre propiedades verificadas en ${state.name}. Compra con confianza con WakaCasa — agentes verificados, propiedades verificadas, transacciones rastreadas.`
              : `Discover verified properties in ${state.name}. Buy with confidence on WakaCasa — verified agents, verified properties, tracked transactions.`}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl px-5 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{language === 'es' ? 'Propiedades' : 'Listings'}</div>
              <div className="font-heading text-2xl font-bold text-[#D4AF37]" data-testid="state-listing-count">{listing_count}</div>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl px-5 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{language === 'es' ? 'Ciudades' : 'Cities'}</div>
              <div className="font-heading text-2xl font-bold text-white">{state.city_count}</div>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl px-5 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{language === 'es' ? 'Agentes Top' : 'Top Agents'}</div>
              <div className="font-heading text-2xl font-bold text-white">{top_agents.length}</div>
            </div>
            <Link to={`/search?state=${state.key}`}>
              <Button className="h-12 px-6 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-xl font-semibold" data-testid="state-browse-all-btn">
                {language === 'es' ? 'Ver todas las propiedades' : 'Browse all listings'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CITIES STRIP */}
      {populatedCities.length > 0 && (
        <section data-testid="state-cities-section">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">{language === 'es' ? 'Ciudades populares' : 'Popular cities'}</h2>
              <p className="text-sm text-slate-500 mt-1">{language === 'es' ? `Explora ${state.name} por ciudad` : `Explore ${state.name} by city`}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {populatedCities.map(c => (
              <Link key={c.key} to={`/search?state=${state.key}&city=${encodeURIComponent(c.name)}`} data-testid={`state-city-card-${c.key}`}>
                <Card className="group hover:border-[#D4AF37] hover:shadow-md transition-all cursor-pointer">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 group-hover:text-[#0B192C]">{c.name}</h3>
                        <p className="text-xs text-slate-500 mt-1">{c.listing_count} {language === 'es' ? 'propiedades' : 'listings'}</p>
                      </div>
                      <div className="w-9 h-9 rounded-full bg-slate-50 group-hover:bg-[#D4AF37]/10 flex items-center justify-center transition-colors">
                        <Building2 className="w-4 h-4 text-slate-400 group-hover:text-[#D4AF37]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          {otherCities.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="state-other-cities">
              <span className="text-xs text-slate-400 mr-1 mt-1.5">{language === 'es' ? 'También en:' : 'Also in:'}</span>
              {otherCities.map(c => (
                <Link key={c.key} to={`/search?state=${state.key}&city=${encodeURIComponent(c.name)}`}>
                  <Badge variant="outline" className="text-xs hover:bg-slate-50 cursor-pointer">{c.name}</Badge>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* TOP-RATED AGENTS */}
      {top_agents.length > 0 && (
        <section data-testid="state-top-agents-section">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#D4AF37]" />
                {language === 'es' ? 'Agentes mejor calificados' : 'Top-rated agents'}
              </h2>
              <p className="text-sm text-slate-500 mt-1">{language === 'es' ? `Activos en ${state.name}` : `Active in ${state.name}`}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {top_agents.map(agent => {
              const tier = TRUST_TIER[agent.trust?.tier] || TRUST_TIER.building;
              const TierIcon = tier.Icon;
              return (
                <Link key={agent.id} to={`/agent/${agent.id}`} data-testid={`state-agent-card-${agent.id}`}>
                  <Card className="group hover:border-[#D4AF37] hover:shadow-md transition-all cursor-pointer h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0B192C] to-[#1A2C4E] flex items-center justify-center text-[#D4AF37] font-heading text-lg font-bold">
                          {agent.name?.[0] || 'A'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-slate-900 truncate">{agent.name}</h3>
                          <div className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${tier.color}`}>
                            <TierIcon className="w-3.5 h-3.5" />
                            <span>{agent.trust?.score ?? 0} · {agent.trust?.tier_label || ''}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            {agent.state_listings} {language === 'es' ? 'propiedades en' : 'listings in'} {state.name}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* FEATURED PROPERTIES */}
      {properties.length > 0 && (
        <section data-testid="state-properties-section">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">{language === 'es' ? 'Propiedades destacadas' : 'Featured listings'}</h2>
              <p className="text-sm text-slate-500 mt-1">{language === 'es' ? 'Las más recientes' : 'The latest in'} {state.name}</p>
            </div>
            <Link to={`/search?state=${state.key}`} className="text-sm font-medium text-[#0B192C] hover:text-[#D4AF37] flex items-center gap-1" data-testid="state-view-all-link">
              {language === 'es' ? 'Ver todas' : 'View all'} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {properties.map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
        </section>
      )}

      {/* EMPTY STATE */}
      {properties.length === 0 && (
        <section className="text-center py-16 bg-slate-50 rounded-2xl" data-testid="state-empty-state">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-slate-700">
            {language === 'es' ? `Aún no hay propiedades en ${state.name}` : `No properties in ${state.name} yet`}
          </h3>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            {language === 'es'
              ? '¿Eres agente? Sé el primero en publicar en este estado.'
              : 'Are you an agent? Be the first to list in this state.'}
          </p>
          <Link to="/submit">
            <Button className="mt-5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="state-empty-cta">
              {language === 'es' ? 'Publicar propiedad' : 'List a property'}
            </Button>
          </Link>
        </section>
      )}
    </div>
  );
}
